/**
 * CanonicalDocument -> ProseMirror JSON (Part 1.2-1.3 of the WYSIWYG editor plan).
 *
 * DocumentProperties/Footnotes/Endnotes/Monikers/SchemaVersion are NOT part of the returned PM
 * doc — they are editor-level state the caller threads through separately (Footnote/Endnote
 * bodies are converted with the exact same blocksToPm used for section content, just not wrapped
 * in the top-level `doc`; see toCdm.ts's docToCdm for how they're recombined on save).
 *
 * A block/inline this build's schema doesn't structurally model becomes an unknownBlock/
 * unknownInline atom carrying the original JSON verbatim in its `json` attr — never silently
 * dropped. See schema.ts's file-level comment for the "attrs ARE the model" design principle this
 * relies on to keep the conversion close to a structural reshape rather than a lossy translation.
 */
import type { Comment as CdmComment } from './types'
import type { JSONContent } from '@tiptap/core'
import type {
  CanonicalDocument, DocSection, DocBlock, DocInline, Footnote, Endnote, DocumentLocalization,
} from './types'
import { defaultParagraphFormat } from './types'

export interface EditorDocument {
  pmDoc: JSONContent
  schemaVersion: number
  properties: CanonicalDocument['properties']
  footnotes: Footnote[]
  endnotes: Endnote[]
  comments: CdmComment[]
  /** Carried through the editor untouched by ProseMirror; edited by the Translations panel. */
  localization?: DocumentLocalization | null
}

export function cdmToDoc(doc: CanonicalDocument): EditorDocument {
  return {
    pmDoc: { type: 'doc', content: doc.sections.map(sectionToPm) },
    schemaVersion: doc.schemaVersion,
    properties: doc.properties,
    footnotes: doc.footnotes,
    endnotes: doc.endnotes,
    comments: doc.comments,
    localization: doc.localization ?? null,
  }
}

function sectionToPm(section: DocSection): JSONContent {
  return {
    type: 'section',
    attrs: {
      page: section.page,
      header: section.header,
      footer: section.footer,
      firstPageHeader: section.firstPageHeader,
      firstPageFooter: section.firstPageFooter,
      evenPageHeader: section.evenPageHeader,
      evenPageFooter: section.evenPageFooter,
      footnoteSetup: section.footnoteSetup,
      endnoteSetup: section.endnoteSetup,
    },
    content: ensureNonEmptyBlocks(blocksToPm(section.body)),
  }
}

/** Exported for footnote/endnote body conversion (identical shape to section body) and testing. */
export function blocksToPm(blocks: DocBlock[]): JSONContent[] {
  return blocks.map(blockToPm)
}

function blockToPm(block: DocBlock): JSONContent {
  switch (block.$type) {
    case 'paragraph':
      return { type: 'paragraph', attrs: { format: block.format }, content: inlinesToPm(block.inlines) }

    case 'heading':
      return { type: 'heading', attrs: { level: block.level, format: block.format }, content: inlinesToPm(block.inlines) }

    case 'list':
      if (block.items.length === 0) return unknownBlockNode(block)
      return {
        type: 'list',
        attrs: { ordered: block.ordered, startAt: block.startAt },
        content: block.items.map(item => ({
          type: 'listItem',
          attrs: { level: item.level, numberFormat: item.numberFormat },
          content: ensureNonEmptyBlocks(blocksToPm(item.content)),
        })),
      }

    case 'table':
      return tableToPm(block)

    case 'image':
      return {
        type: 'image',
        attrs: {
          blobRef: block.blobRef,
          widthPt: block.widthPt,
          heightPt: block.heightPt,
          altText: block.altText,
          sourceMoniker: block.sourceMoniker,
          resolvedSource: block.resolvedSource,
          wrap: block.wrap,
          anchor: block.anchor ?? null,
          svgBlobRef: block.svgBlobRef ?? null,
          // Tiptap Image's own attrs, for it to actually display something in the editor.
          src: block.resolvedSource ?? '',
          alt: block.altText ?? null,
        },
      }

    case 'pageBreak':
      return { type: 'pageBreak' }

    case 'horizontalRule':
      return { type: 'horizontalRule' }

    case 'textBox':
      return {
        type: 'textBox',
        attrs: {
          widthPt: block.widthPt,
          heightPt: block.heightPt,
          fillHex: block.fillHex,
          border: block.border,
          wrap: block.wrap,
          anchor: block.anchor ?? null,
          vAlign: block.vAlign,
          paddingLeftPt: block.paddingLeftPt,
          paddingRightPt: block.paddingRightPt,
          paddingTopPt: block.paddingTopPt,
          paddingBottomPt: block.paddingBottomPt,
          name: block.name,
        },
        // `block+` content, so an empty box still needs one paragraph to be a valid node.
        content: ensureNonEmptyBlocks(blocksToPm(block.content)),
      }

    case 'toc':
      return { type: 'toc', attrs: { content: block.content } }

    case 'embeddedObject':
      return {
        type: 'embeddedObject',
        attrs: {
          kind: block.kind, xml: block.xml, widthPt: block.widthPt, heightPt: block.heightPt,
          name: block.name, previewBlobRef: block.previewBlobRef, parts: block.parts,
        },
      }

    case 'contentControl':
      return {
        type: 'contentControl',
        attrs: { control: block.control },
        // `block+` content, so a control whose content the author emptied still needs a paragraph.
        content: ensureNonEmptyBlocks(blocksToPm(block.content)),
      }

    case 'loop':
      if (block.body.length === 0) return unknownBlockNode(block)
      return {
        type: 'loop',
        attrs: { rawName: block.rawName, normalizedKey: block.normalizedKey, itemAlias: block.itemAlias, source: block.source },
        content: ensureNonEmptyBlocks(blocksToPm(block.body)),
      }

    case 'conditional': {
      if (block.body.length === 0) return unknownBlockNode(block)
      const branches: JSONContent[] = [{ type: 'branch', attrs: { kind: 'body' }, content: ensureNonEmptyBlocks(blocksToPm(block.body)) }]
      if (block.elseBody) branches.push({ type: 'branch', attrs: { kind: 'else' }, content: ensureNonEmptyBlocks(blocksToPm(block.elseBody)) })
      return {
        type: 'conditional',
        attrs: { rawName: block.rawName, normalizedKey: block.normalizedKey, negate: block.negate, source: block.source },
        content: branches,
      }
    }
  }
}

function tableToPm(block: Extract<DocBlock, { $type: 'table' }>): JSONContent {
  return {
    type: 'table',
    attrs: {
      columnWidthsPt: block.columnWidthsPt,
      borders: block.borders,
      alignment: block.alignment,
      width: block.width,
      indentPt: block.indentPt,
      layout: block.layout,
      cellSpacingPt: block.cellSpacingPt,
      propertiesChange: block.propertiesChange,
    },
    content: block.rows.map(row => ({
      type: 'tableRow',
      attrs: {
        heightPt: row.heightPt, heightRule: row.heightRule, cantSplit: row.cantSplit,
        control: row.control, revision: row.revision,
      },
      content: row.cells
        .filter(cell => !cell.isMergedContinuation) // re-synthesized from colspan/rowspan on export — see toCdm.ts
        .map(cell => ({
          type: row.isHeader ? 'tableHeader' : 'tableCell',
          attrs: {
            colspan: cell.colSpan, rowspan: cell.rowSpan, format: cell.format, control: cell.control,
            propertiesChange: cell.propertiesChange, columnLoop: cell.columnLoop ?? null,
          },
          content: ensureNonEmptyBlocks(blocksToPm(cell.content)),
        })),
    })),
  }
}

export function inlinesToPm(inlines: DocInline[]): JSONContent[] {
  // ProseMirror `text` nodes cannot be zero-length (a hard PM constraint) — an empty TextRun
  // carries no visible content, so it is dropped rather than round-tripped through a placeholder
  // character that would corrupt the text on the way back. A documented, minor limitation.
  return inlines
    .filter(inline => inline.$type !== 'run' || inline.text.length > 0)
    .map(inlineToPm)
}

function inlineToPm(inline: DocInline): JSONContent {
  switch (inline.$type) {
    case 'run':
      return { type: 'text', text: inline.text, marks: [{ type: 'runFormat', attrs: { format: inline.format } }] }

    case 'break':
      return { type: 'hardBreak' }

    case 'tab':
      return { type: 'tabStop' }

    case 'placeholder':
      return {
        type: 'placeholder',
        attrs: { rawName: inline.rawName, normalizedKey: inline.normalizedKey, formatters: inline.formatters, format: inline.format, source: inline.source },
      }

    case 'inlineImage':
      // Render-time only on the C# side ([JsonIgnore] on its byte payload) — the editor should
      // never construct or round-trip one of these; fall back to the safety-net atom rather than
      // silently emitting an image with no data.
      return unknownInlineNode(inline)

    case 'field':
      return { type: 'field', attrs: { fieldType: inline.fieldType, staticText: inline.staticText, format: inline.format, instruction: inline.instruction } }

    case 'hyperlink':
      return { type: 'hyperlinkRun', attrs: { text: inline.text, url: inline.url, anchorName: inline.anchorName, format: inline.format } }

    case 'footnote':
      return { type: 'footnoteRef', attrs: { footnoteId: inline.footnoteId } }

    case 'endnote':
      return { type: 'endnoteRef', attrs: { endnoteId: inline.endnoteId } }

    case 'bookmark':
      return { type: 'bookmark', attrs: { name: inline.name } }

    case 'comment':
      return { type: 'comment', attrs: { commentId: inline.commentId, anchor: inline.anchor } }

    case 'contentControl':
      return {
        type: 'contentControlMarker',
        attrs: { controlId: inline.controlId, anchor: inline.anchor, control: inline.control },
      }

    case 'deleted':
      return { type: 'deleted', attrs: { text: inline.text, format: inline.format, revision: inline.revision } }

    case 'equation':
      return {
        type: 'equation',
        attrs: { xml: inline.xml, text: inline.text, isDisplay: inline.isDisplay, format: inline.format },
      }

    case 'conditionalRun':
      // Kept verbatim as one atom (see schema.ts ConditionalRunNode): branch content is not
      // editable in place, so the node round-trips exactly.
      return { type: 'conditionalRun', attrs: { json: inline } }

    case 'loopRun':
      // The same: one atom, kept verbatim (schema.ts LoopRunNode).
      return { type: 'loopRun', attrs: { json: inline } }

    default:
      // A $type this build does not know (a newer server's node) — the switch is exhaustive for
      // the known union, so without this it returned undefined and the node was lost.
      return unknownInlineNode(inline)
  }
}

function unknownBlockNode(block: DocBlock): JSONContent {
  return { type: 'unknownBlock', attrs: { json: block } }
}

function unknownInlineNode(inline: DocInline): JSONContent {
  return { type: 'unknownInline', attrs: { json: inline } }
}

/**
 * ProseMirror content expressions for section/loop/branch/listItem/table-cell bodies all require
 * at least one block (`block+`), matching how Word itself never has a genuinely contentless
 * section/cell/paragraph slot — but CDM's own model permits an empty block list. A from-scratch
 * empty container therefore round-trips to "one empty paragraph" rather than literally staying
 * empty; a documented, deliberate normalization (the same convention DocxCanonicalParser's own
 * write-back paths already apply, e.g. AddFootnotesPart synthesizing an empty paragraph for a
 * footnote with no real content).
 */
function ensureNonEmptyBlocks(blocks: JSONContent[]): JSONContent[] {
  return blocks.length > 0 ? blocks : [{ type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content: [] }]
}
